// based on PHP Name Parser by Josh Fraser (joshfraser.com)
// ported to JavaScript by Mark Pemburn (pemburnia.com)
// released under Apache 2.0 license

export interface ParsedNameType {
  salutation: string;
  firstName: string;
  initials: string;
  lastName: string;
  suffix: string;
}
class NameParse {
  static parse(fullName: string) {
    fullName = fullName.trim();
    let nameParts: string[] = [];
    let lastName: string = "";
    let firstName: string = "";
    let initials: string = "";
    let word: string | null = null;
    let j: number = 0;
    let i: number = 0;

    // split into words, ignoring any words in parentheses
    nameParts = fullName
      .split(" ")
      .filter((namePart) => namePart.indexOf("(") === -1);

    const numWords: number = nameParts.length;
    const salutation: string | false = this.isSalutation(nameParts[0]);
    const suffix: string | false = this.isSuffix(nameParts[numWords - 1]);

    // set the range for the middle part of the name (trim prefixes & suffixes)
    const start: number = salutation ? 1 : 0;
    const end: number = suffix ? numWords - 1 : numWords;

    word = nameParts[start];

    // handle initial as first name
    if (this.isInitial(word)) {
      if (this.isInitial(nameParts[start + 1])) {
        firstName += " " + word.toUpperCase();
      } else {
        initials += " " + word.toUpperCase();
      }
    } else {
      firstName += " " + this.fixCase(word);
    }

    // concat the first name
    for (i = start + 1; i < end - 1; i++) {
      word = nameParts[i];
      if (this.isCompoundLastName(word)) {
        if (!(this.isInitial(word) && word === word.toUpperCase())) {
          break;
        }
      }

      if (this.isInitial(word)) {
        initials += " " + word.toUpperCase();
      } else {
        firstName += " " + this.fixCase(word);
      }
    }

    // check that we have more than 1 word in our string
    if (end - start > 1) {
      for (j = i; j < end; j++) {
        lastName += " " + this.fixCase(nameParts[j]);
      }
    }

    return {
      salutation: salutation || "",
      firstName: firstName.trim(),
      initials: initials.trim(),
      lastName: lastName.trim(),
      suffix: suffix || "",
    };
  }

  static removeIgnoredChars(word: string): string {
    return word.replace(".", "");
  }

  static isSalutation(word: string): string | false {
    word = this.removeIgnoredChars(word).toLowerCase();

    if (word === "mr" || word === "master" || word === "mister") {
      return "Mr.";
    } else if (word === "mrs") {
      return "Mrs.";
    } else if (word === "miss" || word === "ms") {
      return "Ms.";
    } else if (word === "dr") {
      return "Dr.";
    } else if (word === "rev") {
      return "Rev.";
    } else if (word === "fr") {
      return "Fr.";
    } else {
      return false;
    }
  }

  static isSuffix(word: string): string | false {
    word = this.removeIgnoredChars(word).toLowerCase();

    const suffixArray: string[] = [
      "I",
      "II",
      "III",
      "IV",
      "V",
      "Senior",
      "Junior",
      "Jr",
      "Sr",
      "PhD",
      "APR",
      "RPh",
      "PE",
      "MD",
      "MA",
      "DMD",
      "CME",
      "BVM",
      "CFRE",
      "CLU",
      "CPA",
      "CSC",
      "CSJ",
      "DC",
      "DD",
      "DDS",
      "DO",
      "DVM",
      "EdD",
      "Esq",
      "JD",
      "LLD",
      "OD",
      "OSB",
      "PC",
      "Ret",
      "RGS",
      "RN",
      "RNC",
      "SHCJ",
      "SJ",
      "SNJM",
      "SSMO",
      "USA",
      "USAF",
      "USAFR",
      "USAR",
      "USCG",
      "USMC",
      "USMCR",
      "USN",
      "USNR",
    ];

    const suffixIndex: number = suffixArray
      .map((suffix) => suffix.toLowerCase())
      .indexOf(word);

    return suffixIndex >= 0 ? suffixArray[suffixIndex] : false;
  }

  static isCompoundLastName(word: string): boolean {
    word = word.toLowerCase();

    const words: string[] = [
      "vere",
      "von",
      "van",
      "de",
      "del",
      "della",
      "di",
      "da",
      "pietro",
      "vanden",
      "du",
      "st.",
      "st",
      "la",
      "lo",
      "ter",
      "o",
      "o'",
      "mac",
      "fitz",
    ];

    return words.indexOf(word) >= 0;
  }

  static isInitial(word: string): boolean {
    if (!word) {
      return false;
    }

    word = this.removeIgnoredChars(word);

    return word.length === 1;
  }

  static isCamelCase(word: string): boolean {
    const ucReg: RegExp = /[A-Z]+/;
    const lcReg: RegExp = /[a-z]+/;

    return !!(ucReg.exec(word) && lcReg.exec(word));
  }

  static fixCase(word: string): string {
    word = this.safeUcFirst("-", word);
    word = this.safeUcFirst(".", word);

    return word;
  }

  static safeUcFirst(separator: string, word: string): string {
    return word
      .split(separator)
      .map((thisWord) => {
        if (this.isCamelCase(thisWord)) {
          return thisWord;
        } else {
          return (
            thisWord.charAt(0).toUpperCase() + thisWord.slice(1).toLowerCase()
          );
        }
      })
      .join(separator);
  }
}

export default NameParse;
